import { createFileRoute } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { PatternBackground } from "@/app/icons";
import { isProduction } from "@/app/lib/constants";
import {
  Feature,
  isFeatureDisabled,
  isFeatureEnabled,
} from "@/app/lib/utils/featureFlags";
import { FlightCalculator } from "@/app/ui/flights/FlightCalculator";
import Footer from "@/app/ui/Footer";
import { HotelsCalculator } from "@/app/ui/hotels/HotelsCalculator";
import { ScopeSwitcher } from "@/app/ui/ScopeSwitcher";
import { MainSearchWhatsAppBanner } from "@/app/ui/WhatsAppBanners";
import type {
  SearchScope,
  TFlightFormValues,
  THotelFormValues,
} from "@/app/lib/types/types";
import { TripList } from "@/app/ui/trips/TripList";
import { TripCarousel } from "@/app/ui/trips/TripCarousel";
import { useFlightSearchForm } from "@/app/lib/hooks/flightSearch/useFlightSearchForm";
import { useHotelSearchForm } from "@/app/lib/hooks/useHotelSearchForm";
import { isWeb } from "@/app/lib/platform";
import { LogOutButton } from "@/app/ui/web/LogOutButton";
import { BannersPlacement } from "@/app/ui/banners-placement/BannersPlacement";
import { BannersPlacementSlug } from "@/app/lib/types/enums";

const defaultCategory: SearchScope = "flights";

export const Route = createFileRoute("/_authenticated/")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { category?: SearchScope } => {
    if (
      (isFeatureDisabled(Feature.HotelsFlow) && search.category === "hotels") ||
      (isFeatureDisabled(Feature.TripsFlow) && search.category === "trips")
    ) {
      return {
        category: undefined,
      };
    }

    return {
      category: search.category as SearchScope,
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { category } = Route.useSearch();
  const navigate = Route.useNavigate();
  const t = useTranslations();

  const { handleSubmit: handleHotelSearchFormSubmit } = useHotelSearchForm();
  const { handleSubmit: handleFlightSearchFormSubmit } = useFlightSearchForm();

  const handleHotelSearchSubmit = (values: THotelFormValues) => {
    handleHotelSearchFormSubmit(values);
    navigate({ to: "/hotels/list" });
  };

  const handleFlightSearchSubmit = (values: TFlightFormValues) => {
    handleFlightSearchFormSubmit(values);
    navigate({ to: "/flights/departure/list" });
  };

  return (
    <main className="main">
      <div className="relative flex w-full items-center justify-center overflow-clip bg-surface-accent-stronger pt-4 pb-10">
        <PatternBackground aria-hidden className="absolute inset-0" />

        {!isProduction ? (
          <div className="absolute start-2 top-2 flex items-center gap-1 text-white">
            {isWeb ? <LogOutButton /> : null}

            <div className="rounded-full bg-surface-accent-strong px-3 py-2 text-xs capitalize">
              {import.meta.env.VITE_PUBLIC_ENVIRONMENT}
            </div>
          </div>
        ) : null}

        <ScopeSwitcher current={category || defaultCategory} />
      </div>

      <div className="relative -mt-6 rounded-t-4xl bg-surface-background px-4 py-6">
        {category === "hotels" ? (
          <HotelsCalculator
            submitButtonContent={t("hotels.search.searchHotel")}
            onSubmit={handleHotelSearchSubmit}
            analyticsSource="main"
          />
        ) : category === "trips" ? (
          <TripList />
        ) : (
          <FlightCalculator
            submitButtonContent={t("flights.search.searchFlight")}
            onSubmit={handleFlightSearchSubmit}
            analyticsSource="main"
          />
        )}
      </div>

      {category !== "trips" ? (
        <div className="flex flex-col gap-6 pb-20">
          <BannersPlacement slug={BannersPlacementSlug.HOMEPAGE_BELOW_SEARCH} />

          {isFeatureEnabled(Feature.TripsFlow) ? <TripCarousel /> : null}

          <div className="px-4">
            <MainSearchWhatsAppBanner />
          </div>
        </div>
      ) : null}

      <Footer />
    </main>
  );
}
